"use client";

import { useId } from "react";
import { PRICE_RANGES } from "~/lib/price-ranges";
import {
  getAllClientAgeRangeSlugs,
  getClientAgeRangeBySlug,
} from "~/lib/client-age-ranges";
import type { TherapyDirectoryFilters } from "~/lib/therapy-directory-filters";

interface DirectoryFilterOptionsProps {
  filters: TherapyDirectoryFilters;
  isPending: boolean;
  onUpdate: (updates: Record<string, string | undefined>) => void;
  onSelect?: () => void;
}

const optionClassName =
  "inline-flex h-[30px] cursor-pointer items-center rounded-full border border-border bg-background px-3 text-xs text-muted-foreground transition-colors hover:text-foreground peer-focus-visible:outline-hidden peer-focus-visible:ring-2 peer-focus-visible:ring-ring peer-focus-visible:ring-offset-2 peer-disabled:cursor-not-allowed peer-disabled:opacity-60 peer-checked:border-brand peer-checked:bg-brand-action peer-checked:font-medium peer-checked:text-brand-foreground";

const priceOptions: Array<{ value?: string; label: string }> = [
  { label: "Sve cene" },
  ...Object.entries(PRICE_RANGES).map(([slug, range]) => ({
    value: slug,
    label: range.label.replace("Terapeuti ", ""),
  })),
];

const ageOptions: Array<{ value?: string; label: string }> = [
  { label: "18+" },
  ...getAllClientAgeRangeSlugs().flatMap((slug) => {
    const ageRange = getClientAgeRangeBySlug(slug);

    return ageRange
      ? [{ value: slug, label: ageRange.label.replace("Terapeuti za ", "") }]
      : [];
  }),
];

const genderOptions: Array<{ value?: string; label: string }> = [
  { label: "Bilo koji pol" },
  { value: "FEMALE", label: "Žena" },
  { value: "MALE", label: "Muškarac" },
];

export default function DirectoryFilterOptions({
  filters,
  isPending,
  onUpdate,
  onSelect,
}: DirectoryFilterOptionsProps) {
  const idPrefix = useId();
  const selectOption = (updates: Record<string, string | undefined>) => {
    onUpdate(updates);
    onSelect?.();
  };

  return (
    <div className="space-y-4">
      <fieldset>
        <legend className="mb-2 text-xs text-muted-foreground">
          Uzrast klijenta
        </legend>
        <div className="flex flex-wrap gap-1.5">
          {ageOptions.map((option) => {
            const value = option.value ?? "adults";
            const id = `${idPrefix}-age-${value}`;
            return (
              <span key={value}>
                <input
                  id={id}
                  className="peer sr-only"
                  type="radio"
                  name={`${idPrefix}-age`}
                  value={value}
                  checked={filters.ageRange === option.value}
                  disabled={isPending}
                  onChange={() => selectOption({ age: option.value })}
                />
                <label htmlFor={id} className={optionClassName}>
                  {option.label}
                </label>
              </span>
            );
          })}
        </div>
      </fieldset>

      <fieldset>
        <legend className="mb-2 text-xs text-muted-foreground">
          Cena sesije
        </legend>
        <div className="flex flex-wrap gap-1.5">
          {priceOptions.map((option) => {
            const value = option.value ?? "all";
            const id = `${idPrefix}-price-${value}`;
            return (
              <span key={value}>
                <input
                  id={id}
                  className="peer sr-only"
                  type="radio"
                  name={`${idPrefix}-price`}
                  value={value}
                  checked={filters.priceRange === option.value}
                  disabled={isPending}
                  onChange={() => selectOption({ price: option.value })}
                />
                <label htmlFor={id} className={optionClassName}>
                  {option.label}
                </label>
              </span>
            );
          })}
        </div>
      </fieldset>

      <fieldset>
        <legend className="mb-2 text-xs text-muted-foreground">
          Pol terapeuta
        </legend>
        <div className="flex flex-wrap gap-1.5">
          {genderOptions.map((option) => {
            const value = option.value ?? "all";
            const id = `${idPrefix}-gender-${value}`;
            return (
              <span key={value}>
                <input
                  id={id}
                  className="peer sr-only"
                  type="radio"
                  name={`${idPrefix}-gender`}
                  value={value}
                  checked={filters.gender === option.value}
                  disabled={isPending}
                  onChange={() => selectOption({ gender: option.value })}
                />
                <label htmlFor={id} className={optionClassName}>
                  {option.label}
                </label>
              </span>
            );
          })}
        </div>
      </fieldset>
    </div>
  );
}
